import React, { useState, useRef } from 'react';
import { usePortfolioImageUpload } from '../hooks/usePortfolioImageUpload';
import { UserCircle, Camera, Globe, AlignLeft, FormInput, Image, Save, Loader2, Check, AlertCircle, RotateCcw, Search } from 'lucide-react';
import FormFieldList from '../shared/FormFieldList';
import Toggle from '../shared/Toggle';
import TagsInput from '../../ui/TagsInput';

const DesktopPortfolioTab = ({ portfolio, availableTags }) => {
  const {
    data,
    docId,
    setField,
    setAutomation,
    setAutomationTags,
    setCustomFields,
    updateImageField,
    isDirty,
    saving,
    errors,
    saveResult,
    saveChanges,
    discardChanges,
    checkDomainAvailability
  } = portfolio;

  const [domainCheckLoading, setDomainCheckLoading] = useState(false);
  const [domainCheckResult, setDomainCheckResult] = useState(null); // { available, message } or null

  const ownerId = data.ownerId;
  const { uploadAvatar, uploadHeader, uploading } = usePortfolioImageUpload({
    ownerId,
    docId,
    onUrlUpdate: (type, url) => updateImageField(`${type}Image`, url),
  });
  const avatarInputRef = useRef();
  const headerInputRef = useRef();
  const [newFieldValue, setNewFieldValue] = useState('');
  // Add custom field
  const addWebField = () => {
    if (newFieldValue.trim()) {
      const updated = [...(data.customFields || []), newFieldValue.trim()];
      setCustomFields(updated);
      setNewFieldValue('');
    }
  };
  // Remove custom field
  const removeField = (idx) => {
    const updated = (data.customFields || []).filter((_, i) => i !== idx);
    setCustomFields(updated);
  };
  return (
    <div className="max-w-4xl mx-auto">
      <div className="space-y-6 pb-10">
        {/* Global Header */}
        <div className="bg-white border border-notion-border rounded-xl p-6 shadow-card">
          <h3 className="text-sm font-semibold text-notion-text mb-4 flex items-center gap-2">
            <UserCircle className="w-4 h-4 text-stone-400" /> Global Header
          </h3>
          <div className="flex gap-6 mb-4">
            <div className="flex-shrink-0 text-center group cursor-pointer">
              <div
                className="w-20 h-20 bg-stone-50 rounded-full flex items-center justify-center text-stone-400 border border-dashed border-stone-300 group-hover:border-stone-500 transition mb-2 overflow-hidden relative"
                onClick={() => avatarInputRef.current && avatarInputRef.current.click()}
                style={{ cursor: 'pointer' }}
              >
                {data.avatarImage ? (
                  <img src={data.avatarImage} alt="Avatar" className="w-full h-full object-cover rounded-full" />
                ) : (
                  <Camera className="w-6 h-6" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  ref={avatarInputRef}
                  style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files && e.target.files[0];
                    if (file) uploadAvatar(file);
                    e.target.value = '';
                  }}
                />
                {uploading.avatar && (
                  <div className="absolute inset-0 bg-white/60 flex items-center justify-center text-xs">Uploading...</div>
                )}
              </div>
              <span className="text-[10px] text-stone-400 group-hover:text-stone-600">Headshot</span>
            </div>
            <div className="grid grid-cols-2 gap-4 w-full">
              <div>
                <label className="block text-[10px] font-medium text-stone-500 mb-1">Display Name</label>
                <input
                  type="text"
                  value={data.displayName || ''}
                  onChange={e => setField('displayName', e.target.value)}
                  className="w-full bg-notion-sidebar border border-transparent focus:bg-white focus:border-stone-300 rounded px-3 py-2 text-xs outline-none transition"
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-stone-500 mb-1">License #</label>
                <input
                  type="text"
                  value={data.license || ''}
                  onChange={e => setField('license', e.target.value)}
                  className="w-full bg-notion-sidebar border border-transparent focus:bg-white focus:border-stone-300 rounded px-3 py-2 text-xs outline-none transition"
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-stone-500 mb-1">Phone</label>
                <input
                  type="text"
                  value={data.phone || ''}
                  onChange={e => setField('phone', e.target.value)}
                  className="w-full bg-notion-sidebar border border-transparent focus:bg-white focus:border-stone-300 rounded px-3 py-2 text-xs outline-none transition"
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-stone-500 mb-1">Email</label>
                <input
                  type="text"
                  value={data.email || ''}
                  onChange={e => setField('email', e.target.value)}
                  className="w-full bg-notion-sidebar border border-transparent focus:bg-white focus:border-stone-300 rounded px-3 py-2 text-xs outline-none transition"
                />
              </div>
            </div>
          </div>
          <div className="border-t border-notion-border pt-4">
            <label className="block text-[10px] font-medium text-stone-500 mb-2">Header Background Image</label>
            <div className="flex items-center gap-3">
              <button
                className="px-3 py-2 bg-notion-sidebar border border-transparent hover:border-stone-300 rounded text-xs text-stone-600 transition flex items-center gap-2"
                onClick={() => headerInputRef.current && headerInputRef.current.click()}
                type="button"
              >
                <Image className="w-3.5 h-3.5" /> Upload Image
              </button>
              <input
                type="file"
                accept="image/*"
                ref={headerInputRef}
                style={{ display: 'none' }}
                onChange={e => {
                  const file = e.target.files && e.target.files[0];
                  if (file) uploadHeader(file);
                  e.target.value = '';
                }}
              />
              <span className="text-[10px] text-stone-400">Recommended: 1920x400px</span>
              {uploading.header && <span className="text-[10px] text-stone-400">Uploading...</span>}
            </div>
            {data.headerImage && (
              <div className="mt-2 w-full h-24 rounded-lg overflow-hidden border border-stone-200">
                <img src={data.headerImage} alt="Header" className="w-full h-full object-cover" />
              </div>
            )}
          </div>
        </div>

        {/* Custom Domain */}
        <div className="bg-white border border-notion-border rounded-xl p-6 shadow-card">
          <div className="flex justify-between items-start mb-2">
            <h3 className="text-sm font-semibold text-notion-text flex items-center gap-2">
              <Globe className="w-4 h-4 text-stone-400" /> Custom Domain
            </h3>
          </div>
          <p className="text-[11px] text-stone-500 leading-relaxed mb-4">
            Use your own domain name (e.g. sarahjenkins.com) for your profile. First buy a domain from a registrar like GoDaddy or Namecheap, then add it here.
          </p>
          <div className="space-y-2">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="yourdomain.com or subdomain"
                value={data.customDomain || ''}
                onChange={e => { setField('customDomain', e.target.value); setDomainCheckResult(null); }}
                className={`flex-1 bg-notion-sidebar border ${errors.customDomain ? 'border-red-400 focus:border-red-500' : 'border-transparent focus:border-stone-300'} focus:bg-white rounded px-3 py-2 text-xs outline-none transition`}
              />
              <button
                type="button"
                onClick={async () => {
                  setDomainCheckLoading(true);
                  setDomainCheckResult(null);
                  try {
                    const result = await checkDomainAvailability();
                    setDomainCheckResult(result);
                  } finally {
                    setDomainCheckLoading(false);
                  }
                }}
                disabled={domainCheckLoading || !data.customDomain?.trim()}
                className="flex items-center gap-1.5 px-3 py-2 text-[11px] font-medium rounded border border-stone-300 hover:bg-stone-50 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {domainCheckLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Search className="w-3.5 h-3.5" />
                )}
                {domainCheckLoading ? 'Checking...' : 'Check Availability'}
              </button>
            </div>
            {errors.customDomain && (
              <div className="flex items-center gap-1.5 text-red-600 text-[11px]">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{errors.customDomain}</span>
              </div>
            )}
            {!errors.customDomain && domainCheckResult?.available && (
              <div className="flex items-center gap-1.5 text-green-600 text-[11px]">
                <Check className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{domainCheckResult.message}</span>
              </div>
            )}
            <p className="text-[10px] text-stone-400">
              Enter a subdomain (e.g. "sarah") or full domain (e.g. "sarahjenkins.com"). Domain uniqueness is enforced.
            </p>
          </div>
        </div>

        {/* About Us & Content */}
        <div className="bg-white border border-notion-border rounded-xl p-6 shadow-card">
          <h3 className="text-sm font-semibold text-notion-text mb-4 flex items-center gap-2">
            <AlignLeft className="w-4 h-4 text-stone-400" /> About Us & Content
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-[10px] font-medium text-stone-500 mb-1">Section Title</label>
              <input
                type="text"
                value={data.aboutTitle || ''}
                onChange={e => setField('aboutTitle', e.target.value)}
                className="w-full bg-notion-sidebar border border-transparent focus:bg-white focus:border-stone-300 rounded px-3 py-2 text-xs outline-none transition font-medium"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-stone-500 mb-1">Body Text</label>
              <textarea
                rows="5"
                className="w-full bg-notion-sidebar border border-transparent focus:bg-white focus:border-stone-300 rounded px-3 py-2 text-xs outline-none transition resize-none leading-relaxed text-stone-600"
                value={data.aboutBody || ''}
                onChange={e => setField('aboutBody', e.target.value)}
              />
            </div>
            <Toggle
              label="Show Active Listings"
              subLabel="Display property feed on home page"
              checked={!!data.showActiveListings}
              onChange={val => setField('showActiveListings', val)}
              variant="desktop"
            />
          </div>
        </div>

        {/* Contact Form Builder */}
        <div className="bg-white border border-notion-border rounded-xl p-6 shadow-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-notion-text flex items-center gap-2">
              <FormInput className="w-4 h-4 text-stone-400" /> Contact Form Builder
            </h3>
          </div>

          <FormFieldList
            customFields={data.customFields || []}
            newFieldValue={newFieldValue}
            setNewFieldValue={setNewFieldValue}
            onAddField={addWebField}
            onRemoveField={removeField}
            fieldType="web"
            placeholder="Add custom field (e.g. Budget Range)"
            variant="default"
          />
          <div className="border-t border-notion-border pt-4 mt-4">
            <h4 className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-3">Post-Submission Automations</h4>
            <div className="space-y-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!data.automations?.createLead}
                  onChange={e => setAutomation('createLead', e.target.checked)}
                  className="accent-stone-800 rounded w-3.5 h-3.5"
                />
                <span className="text-xs text-stone-600">Create Lead in CRM</span>
              </label>

              {/* Auto-assign tags when createLead is enabled */}
              {!!data.automations?.createLead && (
                <div className="ml-6 mt-1">
                  <label className="block text-[10px] text-stone-500 font-medium mb-1.5">Auto-assign Tags</label>
                  <TagsInput
                    tags={data.automations?.tags || []}
                    onChange={(newTags) => setAutomationTags(newTags)}
                    suggestions={availableTags}
                    placeholder="Add tag and press Enter..."
                    size="sm"
                  />
                  <p className="text-[10px] text-stone-400 mt-1">Tags will be applied to leads created from the contact form</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Save Actions */}
        <div className="bg-white border border-notion-border rounded-xl p-4 shadow-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {saveResult && (
                <div className={`flex items-center gap-1.5 text-xs ${saveResult.success ? 'text-green-600' : 'text-red-600'}`}>
                  {saveResult.success ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <AlertCircle className="w-4 h-4" />
                  )}
                  <span>{saveResult.success ? 'Changes saved successfully!' : saveResult.error}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isDirty && (
                <button
                  onClick={discardChanges}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-600 bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors disabled:opacity-50"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Discard
                </button>
              )}
              <button
                onClick={saveChanges}
                disabled={!isDirty || saving}
                className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-white bg-stone-800 hover:bg-stone-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-3.5 h-3.5" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesktopPortfolioTab;
